import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import Decimal from "decimal.js";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { API_BASE, api } from "../../api/client";
import type { ReportStaffList, ReportSummary } from "../../api/types";
import { QuoteFinance } from "./QuoteFinance";
import { ReconcilePage } from "../reconcile/ReconcilePage";
import { formatMoney } from "../../lib/money";

// 月 / 季 / 年報與業務績效(第 17 輪)。**這是財務與老闆的頁面。**
//
// --------------------------------------------------------------------------
// 三個日期口徑一定要寫在畫面上
// --------------------------------------------------------------------------
// 營收看發票開立日、成本看花費日、成交看成交日 —— 所以同一段期間的
// 「成交 100 萬」與「營收 30 萬」**不是同一批單**,而財務第一次看到一定會問
// 為什麼。只寫「營收」兩個字的話,那個問題每個月都會再被問一次,
// 而每一次都會有人懷疑是系統算錯了。
//
// --------------------------------------------------------------------------
// 算數全在後端
// --------------------------------------------------------------------------
// 這一頁一個加總都不做。做了的話,畫面上的數字與匯出的 Excel 會各有一份算法,
// 而兩份遲早會漂開 —— 漂開的那天財務交出去的檔案跟老闆螢幕上的對不起來,
// 而兩邊各自看起來都很正常。

/** 「quote」是單張報價單的財務總覽(業主 2026-09-30),不是一段期間。
 *  「reconcile」是對帳(銷項 / 進項總表),2026-09-30 從側邊欄併進來(財務整理 2)。 */
type Kind = "month" | "quarter" | "year" | "quote" | "reconcile";
const KINDS: Kind[] = ["month", "quarter", "year", "quote", "reconcile"];

/**
 * 營收口徑(業主 2026-09-30:「兩種都要,可以切換」)。
 * 公司手開紙本發票,只看發票的話營收永遠是 0 —— 所以**預設依成交**。
 */
export type Basis = "deal" | "invoice";

/** 金額一律走 decimal.js。`Number` 算出來的角分會直接印給人看。 */
function money(value: string | number | null | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

/** 一格大數字。`hint` 是它的日期口徑 —— 見檔頭,這一格不是裝飾。 */
function Figure({
  label,
  hint,
  value,
  tone,
}: {
  label: string;
  hint: string;
  value: React.ReactNode;
  tone?: "ok" | "danger";
}) {
  const color =
    tone === "danger" ? "text-[var(--danger)]" : tone === "ok" ? "text-[var(--ok)]" : "text-[var(--text)]";
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-4 py-3">
      <div className="text-xs font-medium text-[var(--text-2)]">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="mt-1 text-xs text-[var(--text-3)]">{hint}</div>
    </div>
  );
}

export function ReportsPage() {
  const today = useMemo(() => new Date(), []);
  const [search] = useSearchParams();
  const [kind, setKind] = useState<Kind>(() => {
    const tab = search.get("tab") as Kind | null;
    return tab && KINDS.includes(tab) ? tab : "month";
  });
  const [basis, setBasis] = useState<Basis>("deal");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(today.getMonth() / 3) + 1);

  const params = useMemo(() => {
    if (kind === "month") return { period: "month", year, month, basis };
    if (kind === "quarter") return { period: "quarter", year, quarter, basis };
    return { period: "year", year, basis };
  }, [kind, year, month, quarter, basis]);

  const query = useMemo(
    () => new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString(),
    [params],
  );

  const byPeriod = kind !== "quote" && kind !== "reconcile";
  const summary = useQuery({
    queryKey: ["report-summary", query],
    queryFn: () => api.get<ReportSummary>("/reports/summary", params),
    enabled: byPeriod,
  });
  const staff = useQuery({
    queryKey: ["report-staff", query],
    queryFn: () => api.get<ReportStaffList>("/reports/staff", params),
    enabled: byPeriod,
  });

  const s = summary.data;
  const rows = staff.data?.items ?? [];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">財務報表</h2>
        {byPeriod ? (
        <a
          href={`${API_BASE}/reports/summary.xlsx?${query}`}
          className="inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
        >
          <Download aria-hidden="true" size={16} />
          下載報表 XLSX
        </a>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1" role="group" aria-label="期間">
          {(
            [
              ["month", "月報"],
              ["quarter", "季報"],
              ["year", "年報"],
              ["quote", "單張報價單"],
              ["reconcile", "對帳"],
            ] as [Kind, string][]
          ).map(([value, text]) => (
            <Button
              key={value}
              size="sm"
              variant={kind === value ? "primary" : undefined}
              aria-pressed={kind === value}
              onClick={() => setKind(value)}
            >
              {text}
            </Button>
          ))}
        </div>

        {kind !== "reconcile" ? (
          <div className="flex gap-1" role="group" aria-label="營收口徑">
            {(
              [
                ["deal", "依成交"],
                ["invoice", "依發票"],
              ] as [Basis, string][]
            ).map(([value, text]) => (
              <Button
                key={value}
                size="sm"
                variant={basis === value ? "primary" : undefined}
                aria-pressed={basis === value}
                title={value === "deal" ? "成交那天,整張單的總額算營收(管理用)" : "發票開立日(報稅用)"}
                onClick={() => setBasis(value)}
              >
                {text}
              </Button>
            ))}
          </div>
        ) : null}

        {byPeriod ? (
        <>
        <Input
          label="年度"
          type="number"
          className="w-24"
          value={year}
          onChange={(e) => setYear(Number(e.target.value) || today.getFullYear())}
        />

        {kind === "month" ? (
          <Select
            label="月份"
            className="w-24"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            options={Array.from({ length: 12 }, (_, i) => ({
              value: String(i + 1),
              label: `${i + 1} 月`,
            }))}
          />
        ) : null}

        {kind === "quarter" ? (
          <Select
            label="季別"
            className="w-28"
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
            options={[1, 2, 3, 4].map((q) => ({
              value: String(q),
              label: `第 ${q} 季`,
            }))}
          />
        ) : null}
        </>
        ) : null}
      </div>

      {kind === "reconcile" ? <ReconcilePage embedded /> : kind === "quote" ? <QuoteFinance basis={basis} /> : (
      <>

      <p className="m-0 text-sm font-medium text-[var(--text-2)]">{s?.period_label ?? ""}</p>

      {/* 每一格底下都寫出它看的是哪一個日期 —— 見檔頭。 */}
      <div
        role="group"
        aria-label="總覽"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Figure
          label="營收"
          hint={basis === "deal" ? "成交日,整張單的總額(含稅)" : "發票開立日"}
          value={money(s?.revenue)}
        />
        <Figure label="成本" hint="雜支的花費日" value={money(s?.cost)} />
        <Figure
          label="毛利"
          hint="營收 − 成本"
          tone={s && new Decimal(s.profit ?? 0).lessThan(0) ? "danger" : undefined}
          value={money(s?.profit)}
        />
        <Figure
          label="毛利率"
          hint="沒有營收時算不出來"
          value={
            s && s.margin_percent !== null && s.margin_percent !== undefined ? (
              `${s.margin_percent} %`
            ) : (
              // **不是「毛利 0%」** —— 那兩件事不一樣,後者看起來像整個月做白工。
              <span className="text-base font-normal text-[var(--text-3)]">還沒有營收</span>
            )
          }
        />
        <Figure
          label="成交金額"
          hint={
            s && s.deals_with_estimated_date > 0
              ? `成交日(其中 ${s.deals_with_estimated_date} 張是用報價日推的)`
              : "成交日(第一次回簽那天)"
          }
          value={money(s?.deal_amount)}
        />
        <Figure
          label="已收"
          hint={basis === "deal" ? "這段期間成交的單收回來的" : "這段期間開出去的票收回來的"}
          value={money(s?.received)}
        />
        <Figure
          label="未收"
          hint={basis === "deal" ? "同一批單還差的" : "同一批票還差的"}
          value={money(s?.receivable)}
        />
        {/* 路線圖 §3.3:逾期未收**單獨一塊** —— 那是財務每天真正在追的東西。 */}
        <Figure
          label="其中已逾期"
          hint="已過請款日仍未收足"
          tone={s && new Decimal(s.overdue ?? 0).greaterThan(0) ? "danger" : undefined}
          value={money(s?.overdue)}
        />
      </div>

      {/* 舊單沒有狀態事件時成交日只能用報價日推 —— **那幾張可能落在錯的月份**,
          而它們在報表上看起來與正常的單一模一樣。所以要標出來。 */}
      {s && s.deals_with_estimated_date > 0 ? (
        <p
          role="status"
          className="m-0 rounded-[var(--radius)] border border-[var(--warn)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-2)]"
        >
          這段期間有 <strong>{s.deals_with_estimated_date}</strong> 張單查不到「改成已結案」
          那筆紀錄(多半是系統上線前就存在的舊單),成交日是用<strong>報價日</strong>推的 ——
          它們可能落在錯的月份。
        </p>
      ) : null}

      <p className="m-0 text-xs text-[var(--text-3)]">
        {basis === "deal"
          ? "依成交:成交那天整張單算營收,成本看雜支的花費日。報稅請切到「依發票」。"
          : "成交、營收與成本各看各的日期,所以同一段期間的「成交」與「營收」不是同一批單 —— 這個月成交的可能還沒開票,這個月開票的可能是上一季成交的。"}
      </p>

      {s && s.by_category.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="m-0 text-base font-semibold text-[var(--text)]">雜支分類</h3>
          <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
            <table aria-label="雜支分類" className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                  <th scope="col" className="px-3 py-2">分類</th>
                  <th scope="col" className="px-3 py-2">金額</th>
                </tr>
              </thead>
              <tbody>
                {s.by_category.map((row) => (
                  <tr key={row.category} className="border-t border-[var(--border)]">
                    {/* 中文字樣由後端給 —— 前端不自己維護一份對照表。 */}
                    <td className="px-3 py-2">{row.label}</td>
                    <td className="px-3 py-2 tabular-nums">{money(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="m-0 text-xs text-[var(--text-3)]">
            其中未歸戶到任何專案的管銷 {money(s.overhead_cost)} —— 那部分不進任何一個專案的毛利。
          </p>
        </section>
      ) : null}

      {s && s.by_month.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="m-0 text-base font-semibold text-[var(--text)]">逐月明細</h3>
          <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
            <table aria-label="逐月明細" className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                  <th scope="col" className="px-3 py-2">月份</th>
                  <th scope="col" className="px-3 py-2">營收</th>
                  <th scope="col" className="px-3 py-2">成本</th>
                  <th scope="col" className="px-3 py-2">毛利</th>
                  <th scope="col" className="px-3 py-2">成交金額</th>
                </tr>
              </thead>
              <tbody>
                {/* 沒單的月份是一列 0,**不是跳過** —— 跳過看起來像那個月沒發生過。 */}
                {s.by_month.map((row) => (
                  <tr key={`${row.year}-${row.month}`} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2">{row.label}</td>
                    <td className="px-3 py-2 tabular-nums">{money(row.revenue)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(row.cost)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(row.profit)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(row.deal_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="m-0 text-base font-semibold text-[var(--text)]">業務績效</h3>
          <a
            href={`${API_BASE}/reports/staff.xlsx?${query}`}
            className="inline-flex h-8 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
          >
            <Download aria-hidden="true" size={14} />
            下載績效表 XLSX
          </a>
        </div>
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="業務績效" className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                <th scope="col" className="px-3 py-2">同仁</th>
                <th scope="col" className="px-3 py-2">成交金額</th>
                <th scope="col" className="px-3 py-2">件數</th>
                <th scope="col" className="px-3 py-2">營收</th>
                <th scope="col" className="px-3 py-2">成本</th>
                <th scope="col" className="px-3 py-2">毛利</th>
                <th scope="col" className="px-3 py-2">毛利率</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user_id ?? "none"} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {row.name}
                      {/* 離職的仍然在表上 —— 拿掉的話各業務加總會小於公司總營收。 */}
                      {row.active ? null : <Badge tone="neutral">已離職</Badge>}
                      {/* §3.4:成本歸戶不完整要標出來,否則沒報帳的案子會排第一名。 */}
                      {row.quotes_without_cost > 0 ? (
                        <Badge tone="warn">{`${row.quotes_without_cost} 案未報成本`}</Badge>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{money(row.deal_amount)}</td>
                  <td className="px-3 py-2 tabular-nums">{row.deal_count}</td>
                  <td className="px-3 py-2 tabular-nums">{money(row.revenue)}</td>
                  <td className="px-3 py-2 tabular-nums">{money(row.cost)}</td>
                  <td className="px-3 py-2 tabular-nums font-semibold">{money(row.profit)}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {row.margin_percent === null || row.margin_percent === undefined ? (
                      <span className="text-[var(--text-3)]">還沒有營收</span>
                    ) : (
                      `${row.margin_percent} %`
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="m-0 text-xs text-[var(--text-3)]">
          未歸戶的管銷不屬於任何人,所以各列的成本合計會小於公司總成本。
        </p>
      </section>
      </>
      )}
    </div>
  );
}
